// 🔴 VISITE RÉNOVATION — blocs du relevé : note de visite, photos, clôture,
// relevés du dossier (en cours / clos), code d'accès terrain (bureau).
//
// 🔑 Tous passent par l'objet `api` reçu (bureau OU terrain, cf. ./api.js) :
//    ⛔ aucune URL écrite ici, ⛔ aucune route hors `/api/chantier/*`.
// 🔑 NOTE DE VISITE = note du RELEVÉ, ⛔ le commentaire d'un poste.
// 🔑 PHOTOS affichées depuis un BLOB chargé AVEC l'autorisation : ⛔ aucune URL
//    média publique (une URL nue sans jeton ⇒ 401).
// ⛔ Relevé clos ⇒ lecture seule (le serveur refuse de toute façon).
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../components/ui/button";

export function Encart({ ton = "info", testId, children }) {
  return (
    <div data-testid={testId} className="text-sm"
         style={{ background: `var(--oh-${ton}-bg)`, color: `var(--oh-${ton}-text)`,
                  border: `0.5px solid var(--oh-${ton}-border)`, borderRadius: 8,
                  padding: 12, lineHeight: 1.6 }}>
      {children}
    </div>
  );
}

const CARTE = { background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
                borderRadius: 12, padding: 16, display: "grid", gap: 8, color: "var(--oh-text)" };
const PRIMAIRE = { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" };
const SECONDAIRE = { borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent" };
const DISCRET = { borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" };
const heure = (iso) => { try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const date = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso || ""; } };

// ── NOTE DE VISITE ──────────────────────────────────────────────────────────
export function NoteVisite({ api, releveId, lectureSeule }) {
  const [texte, setTexte] = useState("");
  const [enregistre, setEnregistre] = useState(null);   // { texte, modifie_le, rev }
  const [etat, setEtat] = useState("");
  // 🔴 N°894 — conflit : la note a été enregistrée ailleurs depuis son chargement.
  const [conflit, setConflit] = useState(false);
  const [texteNonEnregistre, setTexteNonEnregistre] = useState("");

  const charger = useCallback(async () => {
    const n = await api.lireNote(releveId);
    setTexte(n.texte || ""); setEnregistre(n);
  }, [api, releveId]);

  useEffect(() => {
    let vivant = true;
    api.lireNote(releveId).then((n) => { if (vivant) { setTexte(n.texte || ""); setEnregistre(n); } })
      .catch((err) => vivant && setEtat(err.message));
    return () => { vivant = false; };
  }, [api, releveId]);

  const modifie = enregistre && texte !== (enregistre.texte || "");
  const enregistrer = async () => {
    setEtat("");
    try { setEnregistre(await api.ecrireNote(releveId, texte, enregistre?.rev)); }
    catch (err) {
      // ⛔ fusion, ⛔ nouvel essai automatique : l'humain actualise puis réenregistre.
      if (err?.statut === 409) setConflit(true); else setEtat(err.message);
    }
  };
  const actualiser = async () => {
    setEtat("");
    const saisie = texte;
    try { await charger(); setConflit(false); setTexteNonEnregistre(saisie); }
    catch (err) { setEtat(err.message); }
  };

  return (
    <section data-testid="note-visite" style={CARTE}>
      <div style={{ fontWeight: 500 }}>Note de visite</div>
      {lectureSeule ? (
        <p data-testid="note-visite-lecture" className="text-sm" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
          {texte || "Aucune note."}
        </p>
      ) : (
        <>
          <textarea data-testid="note-visite-texte" rows={4} value={texte} aria-label="Note de visite"
                    placeholder="Accès, contraintes, remarques générales sur la visite…"
                    onChange={(ev) => setTexte(ev.target.value)}
                    style={{ borderRadius: 8, border: "0.5px solid var(--oh-border)", padding: 8,
                             background: "var(--oh-bg)", color: "var(--oh-text)" }} />
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Button data-testid="note-visite-enregistrer" variant="outline" onClick={enregistrer}
                    disabled={!modifie || conflit} style={SECONDAIRE}>
              Enregistrer la note
            </Button>
            <span data-testid="note-visite-statut" className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
              {modifie ? "Modifications non enregistrées"
                : enregistre?.modifie_le ? `Note enregistrée à ${heure(enregistre.modifie_le)}` : ""}
            </span>
          </div>
          {conflit && (
            <Encart ton="error" testId="note-visite-conflit">
              La visite a été modifiée depuis son chargement : votre note n'a pas été enregistrée.
              Actualisez avant de réenregistrer.{" "}
              <Button data-testid="note-visite-actualiser" variant="outline" onClick={actualiser} style={SECONDAIRE}>
                Actualiser la note
              </Button>
            </Encart>
          )}
          {texteNonEnregistre && !conflit && texteNonEnregistre !== texte && (
            <div data-testid="note-visite-non-enregistre" className="text-sm"
                 style={{ whiteSpace: "pre-wrap", color: "var(--oh-text-secondary)" }}>
              Votre texte non enregistré (à reprendre si besoin) : {texteNonEnregistre}
            </div>
          )}
        </>
      )}
      {etat && <Encart ton="error" testId="note-visite-erreur">{etat}</Encart>}
    </section>
  );
}

// ── PHOTOS ──────────────────────────────────────────────────────────────────
/** Réduit une photo de téléphone (souvent 3–8 Mo) avant l'envoi ; sans canvas, l'envoie telle quelle. */
async function reduirePhoto(fichier, cote = 1600) {
  try {
    if (typeof createImageBitmap !== "function" || typeof document === "undefined") return fichier;
    const image = await createImageBitmap(fichier);
    const echelle = Math.min(1, cote / Math.max(image.width, image.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(image.width * echelle);
    canvas.height = Math.round(image.height * echelle);
    canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((ok) => canvas.toBlob(ok, "image/jpeg", 0.82));
    return blob ? new File([blob], (fichier.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : fichier;
  } catch { return fichier; }
}

function Vignette({ api, releveId, photo, onSupprimer, lectureSeule }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let vivant = true, u = null;
    api.photoBlob(releveId, photo.id).then((b) => {
      if (vivant && typeof URL !== "undefined" && URL.createObjectURL) { u = URL.createObjectURL(b); setUrl(u); }
    }).catch(() => {});
    return () => { vivant = false; if (u && URL.revokeObjectURL) URL.revokeObjectURL(u); };
  }, [api, releveId, photo.id]);
  return (
    <figure data-testid="photo-releve" style={{ margin: 0, display: "grid", gap: 4 }}>
      {url ? <img src={url} alt={photo.nom || "Photo"} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 8 }} />
           : <div style={{ aspectRatio: "1", borderRadius: 8, background: "var(--oh-bg)", border: "0.5px solid var(--oh-border)" }} />}
      {!lectureSeule && (
        <Button variant="outline" data-testid={`photo-supprimer-${photo.id}`} onClick={() => onSupprimer(photo.id)}
                aria-label="Supprimer la photo" style={{ ...DISCRET, height: 32 }}>
          Supprimer
        </Button>
      )}
    </figure>
  );
}

export function PhotosReleve({ api, releveId, postes, motPoste = "Poste", lectureSeule }) {
  const [photos, setPhotos] = useState([]);
  const [posteId, setPosteId] = useState("");
  const [etat, setEtat] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const entree = useRef(null);

  const recharger = () => api.photos(releveId).then(setPhotos).catch((err) => setEtat(err.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { recharger(); }, [api, releveId]);

  const deposer = async (ev) => {
    const fichiers = Array.from(ev.target.files || []);
    ev.target.value = "";
    if (!fichiers.length) return;
    setEtat(""); setEnvoi(true);
    try {
      for (const f of fichiers) await api.ajouterPhoto(releveId, await reduirePhoto(f), posteId || null);
      await recharger();
    } catch (err) { setEtat(err.message); } finally { setEnvoi(false); }
  };
  const supprimer = async (pid) => {
    setEtat("");
    try { await api.supprimerPhoto(releveId, pid); await recharger(); } catch (err) { setEtat(err.message); }
  };

  return (
    <section data-testid="photos-releve" style={CARTE}>
      <div style={{ fontWeight: 500 }}>Photos ({photos.length})</div>
      {!lectureSeule && (
        <div style={{ display: "grid", gap: 8 }}>
          {postes?.length > 0 && (
            <select data-testid="photo-poste" aria-label="Rattacher la photo" value={posteId}
                    onChange={(ev) => setPosteId(ev.target.value)}
                    style={{ height: 36, borderRadius: 8, border: "0.5px solid var(--oh-border)", padding: "0 8px",
                             background: "var(--oh-bg)", color: "var(--oh-text)" }}>
              <option value="">Photo générale de la visite</option>
              {postes.map((p) => <option key={p.id} value={p.id}>{motPoste} : {p.designation}</option>)}
            </select>
          )}
          <input ref={entree} data-testid="photo-fichier" type="file" accept="image/*" capture="environment"
                 multiple onChange={deposer} style={{ display: "none" }} aria-label="Ajouter une photo" />
          <Button variant="outline" data-testid="photo-ajouter" disabled={envoi}
                  onClick={() => entree.current?.click()} style={SECONDAIRE}>
            {envoi ? "Envoi en cours…" : "Prendre ou ajouter une photo"}
          </Button>
        </div>
      )}
      {photos.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8 }}>
          {photos.map((p) => <Vignette key={p.id} api={api} releveId={releveId} photo={p}
                                        onSupprimer={supprimer} lectureSeule={lectureSeule} />)}
        </div>
      ) : <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Aucune photo.</p>}
      {etat && <Encart ton="error" testId="photos-erreur">{etat}</Encart>}
    </section>
  );
}

// ── CLÔTURE : récapitulatif PUIS confirmation ───────────────────────────────
export function TerminerVisite({ api, releve, motPoste = "Poste", onClos }) {
  const [recap, setRecap] = useState(null);
  const [etat, setEtat] = useState("");
  const [envoi, setEnvoi] = useState(false);

  const preparer = async () => {
    setEtat("");
    try {
      const [photos, note] = await Promise.all([api.photos(releve.id), api.lireNote(releve.id)]);
      const postes = releve.postes || [];
      setRecap({ postes: postes.length, sansQuantite: postes.filter((p) => p.quantite === null || p.quantite === undefined).map((p) => p.designation),
                 photos: photos.length, note: Boolean((note.texte || "").trim()) });
    } catch (err) { setEtat(err.message); }
  };
  const confirmer = async () => {
    setEtat(""); setEnvoi(true);
    try { onClos(await api.terminer(releve.id)); } catch (err) { setEtat(err.message); } finally { setEnvoi(false); }
  };

  if (!recap) {
    return (
      <section style={{ display: "grid", gap: 8 }}>
        <Button data-testid="terminer-visite" variant="outline" onClick={preparer} style={SECONDAIRE}>
          Terminer la visite
        </Button>
        {etat && <Encart ton="error" testId="terminer-erreur">{etat}</Encart>}
      </section>
    );
  }
  return (
    <section data-testid="recapitulatif-cloture" style={{ ...CARTE, border: "0.5px solid var(--oh-accent)" }}>
      <div style={{ fontWeight: 500 }}>Récapitulatif avant clôture</div>
      <ul className="text-sm" style={{ lineHeight: 1.6 }}>
        <li data-testid="recap-postes">{recap.postes} {motPoste.toLowerCase()}(s) enregistré(s)</li>
        {recap.sansQuantite.length > 0 && (
          <li data-testid="recap-sans-quantite">Sans quantité : {recap.sansQuantite.join(", ")}</li>
        )}
        <li data-testid="recap-photos">{recap.photos} photo(s)</li>
        <li data-testid="recap-note">{recap.note ? "Note de visite renseignée" : "Aucune note de visite"}</li>
      </ul>
      <Encart ton="info">Une fois close, la visite ne pourra plus être modifiée ; elle restera consultable.</Encart>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Button data-testid="confirmer-cloture" onClick={confirmer} disabled={envoi} style={PRIMAIRE}>
          Confirmer la clôture
        </Button>
        <Button data-testid="annuler-cloture" variant="outline" onClick={() => setRecap(null)} disabled={envoi} style={DISCRET}>
          Annuler
        </Button>
      </div>
      {etat && <Encart ton="error" testId="terminer-erreur">{etat}</Encart>}
    </section>
  );
}

// ── RELEVÉS DU DOSSIER (en cours / clos) ────────────────────────────────────
export function RelevesDuDossier({ releves, actif, onConsulter }) {
  if (!releves?.length) return null;
  return (
    <section data-testid="releves-dossier" style={{ display: "grid", gap: 4 }}>
      <div className="text-sm" style={{ color: "var(--oh-text-secondary)", fontWeight: 500 }}>Relevés du dossier</div>
      {releves.map((r) => (
        <button key={r.id} type="button" data-testid={`releve-${r.etat}`} onClick={() => onConsulter(r.id)}
                style={{ textAlign: "left", borderRadius: 8, padding: 8, cursor: "pointer",
                         border: `0.5px solid ${r.id === actif ? "var(--oh-accent)" : "var(--oh-border)"}`,
                         background: r.id === actif ? "var(--oh-accent-bg)" : "transparent",
                         color: r.id === actif ? "var(--oh-accent-text)" : "var(--oh-text)" }}>
          {r.etat === "clos" ? `Relevé clos le ${date(r.clos_le)}` : "Relevé en cours"} · {r.nombre_postes} poste(s)
        </button>
      ))}
    </section>
  );
}
